import { useState, useEffect, useCallback } from 'react';
import { getCreditBalance, hasSufficientCredits } from '@/services/billingService';
import type { CreditBalance, CreditActionType } from '@/types/billing';
import { CREDIT_COSTS } from '@/types/billing';
import { useAuth } from '@/contexts/AuthContext';

interface UseCreditsResult {
  balance: CreditBalance | null;
  loading: boolean;
  error: string | null;
  totalCredits: number;
  /** Check if user has enough credits for an action */
  canAfford: (action: CreditActionType) => boolean;
  /** Check if user has enough credits for a specific amount */
  canAffordAmount: (amount: number) => boolean;
  /** Get the cost of an action */
  getCost: (action: CreditActionType) => number;
  /** Refresh balance from server, returns the new balance */
  refreshBalance: (bustCache?: boolean) => Promise<CreditBalance | null>;
  /** Check credits before action, returns true if sufficient */
  checkCredits: (action: CreditActionType) => Promise<boolean>;
}

/**
 * Hook for managing and checking credit balance
 * Use this in components that need to verify credits before actions
 */
export function useCredits(): UseCreditsResult {
  const { isAuthenticated } = useAuth();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = useCallback(async (bustCache = false): Promise<CreditBalance | null> => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      console.log('[useCredits] User not authenticated, skipping balance fetch');
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('[useCredits] Fetching credit balance... (bustCache:', bustCache, ')');
      const data = await getCreditBalance(bustCache);
      console.log('[useCredits] Received balance:', data);
      setBalance(data);
      console.log('[useCredits] Balance state updated');
      return data;
    } catch (err) {
      console.error('Failed to fetch credit balance:', err);
      setError('Failed to load credit balance');
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const totalCredits = balance?.totalCredits ?? 0;

  const getCost = useCallback((action: CreditActionType): number => {
    const costMap: Record<CreditActionType, number> = {
      CreateSurvey: CREDIT_COSTS.createSurvey,
      CreateExam: CREDIT_COSTS.createExam,
      CreateQuiz: CREDIT_COSTS.createQuiz,
      CreateAssessment: CREDIT_COSTS.createAssessment,
      AddQuestion: CREDIT_COSTS.addQuestion,
      ReceiveResponse: CREDIT_COSTS.receiveResponse,
      AiAnalysis: CREDIT_COSTS.aiAnalysis,
      ExportData: CREDIT_COSTS.exportData,
    };
    return costMap[action] ?? 0;
  }, []);

  const canAfford = useCallback(
    (action: CreditActionType): boolean => {
      const cost = getCost(action);
      return totalCredits >= cost;
    },
    [totalCredits, getCost]
  );

  const canAffordAmount = useCallback(
    (amount: number): boolean => {
      return totalCredits >= amount;
    },
    [totalCredits]
  );

  const checkCredits = useCallback(
    async (action: CreditActionType): Promise<boolean> => {
      const cost = getCost(action);
      try {
        return await hasSufficientCredits(cost);
      } catch {
        // If check fails, use local balance
        return totalCredits >= cost;
      }
    },
    [getCost, totalCredits]
  );

  return {
    balance,
    loading,
    error,
    totalCredits,
    canAfford,
    canAffordAmount,
    getCost,
    refreshBalance: fetchBalance,
    checkCredits,
  };
}

/**
 * Get credit cost for a specific action type
 */
export function getCreditCostForAction(action: CreditActionType): number {
  const costMap: Record<CreditActionType, number> = {
    CreateSurvey: CREDIT_COSTS.createSurvey,
    CreateExam: CREDIT_COSTS.createExam,
    CreateQuiz: CREDIT_COSTS.createQuiz,
    CreateAssessment: CREDIT_COSTS.createAssessment,
    AddQuestion: CREDIT_COSTS.addQuestion,
    ReceiveResponse: CREDIT_COSTS.receiveResponse,
    AiAnalysis: CREDIT_COSTS.aiAnalysis,
    ExportData: CREDIT_COSTS.exportData,
  };
  return costMap[action] ?? 0;
}

export default useCredits;
